import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Bell } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { format } from 'date-fns';
import { useReminderCounts } from '@/contexts/ReminderCountsContext';

interface QueuedReminder {
  id: string;
  reminder_type: string;
  channel: string;
  send_at: string;
}

interface ReminderStatusBadgeProps {
  ticketId: string;
}

export const ReminderStatusBadge = ({ ticketId }: ReminderStatusBadgeProps) => {
  const { remindersByTicket } = useReminderCounts();
  
  // Use shared context data if available (non-empty map means provider is mounted)
  const contextReminders = remindersByTicket.get(ticketId);
  
  // Fallback: one-time fetch if context has no data and no provider is mounted
  const [fetchedReminders, setFetchedReminders] = useState<QueuedReminder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If context provider is active (map exists with any data or is simply mounted), skip fetch
    if (remindersByTicket.size > 0 || contextReminders !== undefined) {
      setLoading(false);
      return;
    }
    if (!ticketId) return;

    const fetchOnce = async () => {
      try {
        const { data, error } = await supabase
          .from('reminder_queue')
          .select('id, reminder_type, channel, send_at')
          .eq('ticket_id', ticketId)
          .eq('status', 'queued')
          .order('send_at', { ascending: true });

        if (error) throw error;
        setFetchedReminders(data || []);
      } catch (error) {
        console.error('Failed to fetch queued reminders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOnce();
  }, [ticketId, remindersByTicket, contextReminders]);

  const queuedReminders = contextReminders ?? fetchedReminders;

  if (loading || queuedReminders.length === 0) {
    return null;
  }

  const count = queuedReminders.length;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="secondary"
            className="gap-1"
            data-testid="reminder-status-badge"
          >
            <Bell className="h-3 w-3" />
            {count} reminder{count !== 1 ? 's' : ''}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="space-y-1 text-xs">
            <div className="font-medium">Upcoming Reminders:</div>
            {queuedReminders.slice(0, 3).map((reminder) => (
              <div key={reminder.id}>
                • {reminder.reminder_type} ({reminder.channel}) - {format(new Date(reminder.send_at), 'MMM d, h:mm a')}
              </div>
            ))}
            {count > 3 && (
              <div className="text-muted-foreground">
                +{count - 3} more
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
